import { getAdminQrCodesQuery } from '@/api/qr-codes/qrCodeQueries';
import type { QrCodeFilters } from '@/api/qr-codes/qrCodeTypes';
import { Button } from '@/components/common/Button';
import { RelationActionButton } from '@/components/common/RelationActionButton';
import { TableCell, TableHead } from '@/components/common/Table';
import { Role } from '@/domain/Role';
import { useHasRole } from '@/hooks/useHasRole';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { DashboardTableAccordion } from '../DashboardTableAccordion';
import { CreateQrCodeDialog } from './CreateQrCodeDialog';
import type {
  AppliedQrCodeFilters,
  QrCodeCategory,
} from './ManageQrCodesFilters';
import { ManageQrCodesFilters } from './ManageQrCodesFilters';

const INITIAL_QR_FILTERS: AppliedQrCodeFilters = {
  category: 'doctors',
  name: '',
  dateFrom: '',
  dateTo: '',
};

const CATEGORY_TYPE_MAP: Record<QrCodeCategory, string | undefined> = {
  doctors: 'Doctor',
  clinics: 'Clinic',
  custom: 'Custom',
};

const CATEGORY_NAME_LABEL: Record<QrCodeCategory, string> = {
  doctors: 'Doctor',
  clinics: 'Clinic',
  custom: 'Name',
};

export default function ManageQrCodesTable({
  accordionOpenValue,
}: {
  accordionOpenValue?: string;
}) {
  const { hasRole } = useHasRole();
  const isDoctor = hasRole(Role.DOCTOR);

  const [page, setPage] = useState(1);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] =
    useState<AppliedQrCodeFilters>(INITIAL_QR_FILTERS);
  const [filtersResetKey, setFiltersResetKey] = useState(0);

  const handleAccordionOpen = useCallback(() => {
    setAppliedFilters(INITIAL_QR_FILTERS);
    setPage(1);
    setFiltersResetKey((k) => k + 1);
  }, []);

  const apiFilters: QrCodeFilters = {
    type: CATEGORY_TYPE_MAP[appliedFilters.category],
    ...(appliedFilters.name && { name: appliedFilters.name }),
    ...(appliedFilters.dateFrom && { date_from: appliedFilters.dateFrom }),
    ...(appliedFilters.dateTo && { date_to: appliedFilters.dateTo }),
  };

  const { data: response, isLoading } = useQuery(
    getAdminQrCodesQuery(page, apiFilters),
  );

  const qrCodes = response?.data ?? [];
  const meta = response?.meta;

  const handleCopyLink = (link: string) => {
    navigator.clipboard.writeText(link);
    toast.success('Quiz link copied to clipboard');
  };

  const handleDownload = (base64: string) => {
    const base64Data = base64.includes(',') ? base64.split(',')[1] : base64;
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Uint8Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const blob = new Blob([byteNumbers], { type: 'image/png' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `image.png`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleFiltersApply = (filters: AppliedQrCodeFilters) => {
    setAppliedFilters(filters);
    setPage(1);
  };

  return (
    <>
      <DashboardTableAccordion
        value="manage-qr-codes"
        title="Manage QR codes"
        accordionOpenValue={accordionOpenValue}
        onOpen={handleAccordionOpen}
        data={qrCodes}
        isLoading={isLoading}
        emptyMessage="No QR codes found."
        loadingMessage="Loading QR codes..."
        paginationMeta={meta}
        onPageChange={setPage}
        filtersComponent={
          <ManageQrCodesFilters
            key={filtersResetKey}
            isDoctor={isDoctor}
            onApply={handleFiltersApply}
          />
        }
        footerActions={
          <div className="flex justify-end">
            <Button
              variant="secondary"
              size="md"
              onClick={() => setCreateDialogOpen(true)}
            >
              Add new QR code
            </Button>
          </div>
        }
        renderHeader={() => (
          <>
            <TableHead>
              {CATEGORY_NAME_LABEL[appliedFilters.category]}
            </TableHead>
            <TableHead>Clinic</TableHead>
            <TableHead>Quiz link</TableHead>
            <TableHead>Generated QRs</TableHead>
            <TableHead>Active QRs</TableHead>
            <TableHead className="text-right"></TableHead>
          </>
        )}
        renderRow={(entry) => {
          const clinicName =
            entry.doctor_clinics && entry.doctor_clinics.length > 0
              ? entry.doctor_clinics.map((c) => c.name).join(', ')
              : (entry.clinic?.name ?? '—');

          const displayName = entry.doctor
            ? `${entry.doctor.name} ${entry.doctor.surname}`
            : entry.clinic
              ? entry.clinic.name
              : (entry.name ?? '—');

          return (
            <>
              <TableCell>{displayName}</TableCell>
              <TableCell>{clinicName}</TableCell>
              <TableCell>
                <RelationActionButton onClick={() => handleCopyLink(entry.url)}>
                  Copy link
                </RelationActionButton>
              </TableCell>
              <TableCell>{entry.usage_count}</TableCell>
              <TableCell>{entry.notes_count}</TableCell>
              <TableCell className="text-right">
                <RelationActionButton
                  onClick={() => handleDownload(entry.qr_code_base64)}
                >
                  Download
                </RelationActionButton>
              </TableCell>
            </>
          );
        }}
      />

      <CreateQrCodeDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
      />
    </>
  );
}
